import {useTranslation} from 'react-i18next';
import {labelBarcodeBits} from '@/lib/labelDocument';
import type {LabelField} from '@hisense/shared';
import {fontCell,parseTspl,type TsplBarcodeOp,type TsplTextOp} from '@/lib/tsplPreview';

/**
 * Vẽ lại tem ĐÚNG BẰNG lệnh đã gửi cho máy in.
 *
 * Khung SVG đặt theo milimet thật của tem, hệ toạ độ bên trong là dot 203 dpi
 * — đúng lưới máy in dùng. Nội dung tràn ra ngoài mép tem bị khung cắt, giống
 * hệt máy in: phần nằm ngoài tờ tem thì không in ra được.
 *
 * Giấy trắng mực đen lấy từ token --color-label-*, không đổi theo chủ đề sáng
 * tối: đây là tờ tem vật lý, không phải một khối giao diện.
 */

/** Chân chữ của font bitmap nằm khoảng 80% ô ký tự; TSPL neo chữ ở MÉP TRÊN. */
const BASELINE = 0.8;
/** Khoảng cách từ đáy mã vạch tới dòng chữ đọc được, theo lệ máy TSPL. */
const READABLE_GAP = 4;

function Text({op}:{op:TsplTextOp}){
 const cell=fontCell(op.font);
 const w=cell.w*op.xMul,h=cell.h*op.yMul;
 return <text
  x={op.x} y={op.y+h*BASELINE}
  fontFamily="var(--font-mono)" fontSize={h*BASELINE}
  textLength={op.text.length>0?w*op.text.length:undefined} lengthAdjust="spacingAndGlyphs"
  fill="var(--color-label-ink)"
  transform={op.rotation?`rotate(${op.rotation} ${op.x} ${op.y})`:undefined}>{op.text}</text>;
}

function Barcode({op}:{op:TsplBarcodeOp}){
 const bits=op.codeType==='128'?labelBarcodeBits(op.text):null;
 const cell=fontCell('1');
 if(!bits)return <Text op={{type:'text',x:op.x,y:op.y,font:'1',rotation:op.rotation,xMul:1,yMul:1,text:op.text}}/>;
 const bars:Array<{x:number;w:number}>=[];
 for(let i=0;i<bits.length;){
  if(bits[i]==='0'){i++;continue;}
  const start=i;
  while(bits[i]==='1')i++;
  bars.push({x:op.x+start*op.narrow,w:(i-start)*op.narrow});
 }
 const width=bits.length*op.narrow;
 return <g transform={op.rotation?`rotate(${op.rotation} ${op.x} ${op.y})`:undefined} fill="var(--color-label-ink)">
  {bars.map(b=><rect key={b.x} x={b.x} y={op.y} width={b.w} height={op.height}/>)}
  {op.readable&&<text
   x={op.x+width/2} y={op.y+op.height+READABLE_GAP+cell.h*BASELINE} textAnchor="middle"
   fontFamily="var(--font-mono)" fontSize={cell.h*BASELINE}>{op.text}</text>}
 </g>;
}

export function LabelPreview({payload,omitted=[]}:{payload:string;omitted?:readonly LabelField[]}){
 const {t}=useTranslation();
 const label=parseTspl(payload);
 if(!label)return null;
 return <figure className="m-0">
  <svg role="img" aria-label={t('devices.preview')}
   viewBox={`0 0 ${label.widthDots} ${label.heightDots}`}
   width={`${label.widthMm}mm`} height={`${label.heightMm}mm`}
   className="block max-w-full rounded-xs border border-line-strong shadow-sm"
   style={{background:'var(--color-label-paper)'}}>
   {label.ops.map((op,i)=>op.type==='text'?<Text key={i} op={op}/>:<Barcode key={i} op={op}/>)}
  </svg>
  {omitted.length>0&&<p role="alert" className="mt-2 max-w-xs rounded-sm border border-warn/30 bg-warn-tint p-2 text-2xs">
   <span className="block font-medium">{t('devices.omitted',{fields:omitted.map(f=>t(`devices.fields.${f}`)).join(', ')})}</span>
   {t('devices.omittedHint')}
  </p>}
  <figcaption className="mt-2 text-2xs text-ink-soft">
   <span className="font-mono tabular">{t('devices.sheet',{width:label.widthMm,height:label.heightMm})}</span>
   {label.copies>1&&<> · {t('devices.copies',{count:label.copies})}</>}
   <span className="block">{t('devices.previewNote')}</span>
  </figcaption>
 </figure>;
}
